import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import GalleryAdd from '@/images/GalleryAdd.svg';
import theme from '@/theme';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import IconButton from '@/components/Buttons/IconButton';
import { toast } from '@/components/toast';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { getThumbnailFromFile, resizeFile } from './utils';

const LogoUploadBox = styled.div<{ disabled?: boolean }>`
    border: 1px solid ${({ theme }) => theme.colors.black[200]};
    height: 8rem;
    padding: 1rem;
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.secondary[500]};
    cursor: pointer;
`;

const LogoUploadBoxInner = styled.div`
    display: flex;
    height: 100%;
    align-items: center;
    padding: 8px;
    background-color: ${({ theme }) => theme.colors.black[50]};
    border-radius: 8px;
    gap: 16px;
    overflow: hidden;

    div {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
`;

const LogoThumbnail = styled.img`
    width: 40px;
    height: 40px;
    border-radius: 4px;
`;

const ClearButton = styled(IconButton)`
    margin-left: auto;

    svg {
        width: 24px;
        height: 24px;
    }
`;

const FilenameText = styled(Typography)`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

interface LogoUploadProps {
    file?: File | string;
    onChange: (file?: File) => void;
    disabled?: boolean;
    onClickUpgrade?: () => void;
    onBlur?: () => void;
}

const logoUrlToFile = async (url: string) => {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error('Failed to fetch logo');
    }

    const blob = await response.blob();
    return new File([blob], 'logo');
};

const MAX_IMAGE_SIZE = 5000000;
const MAX_SIZE_MB = MAX_IMAGE_SIZE / 1000000;

export const LogoUpload = React.forwardRef<HTMLInputElement, LogoUploadProps>(
    ({ file: fileSrc, onChange, disabled, onClickUpgrade, onBlur }, ref) => {
        const [file, setFile] = useState<File | undefined>(fileSrc instanceof File ? fileSrc : undefined);
        const [thumbnail, setThumbnail] = useState<string | undefined>();

        useEffect(() => {
            if (typeof fileSrc === 'string') {
                logoUrlToFile(fileSrc).then(setFile);
            } else {
                setFile(fileSrc);
            }
        }, [fileSrc]);

        useEffect(() => {
            if (file) {
                getThumbnailFromFile(file).then(setThumbnail);
            }
        }, [file]);

        const onDrop = async (acceptedFiles: File[], rejectedFiles: any[]) => {
            if (rejectedFiles.some(f => f.errors.some((e: any) => e.code === 'file-too-large'))) {
                toast.error({ title: `File should be up to ${MAX_SIZE_MB}MB `, toastId: `upload-logo-error` });
            }

            const preparedImage = await resizeFile(acceptedFiles[0]);

            onChange(preparedImage);
        };

        const handleClearFile = (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onChange(undefined);
        };

        const { getRootProps, getInputProps } = useDropzone({
            onDrop,
            maxSize: MAX_IMAGE_SIZE,
            accept: {
                'image/*': ['.png', '.jpg', '.jpeg'],
            },
            multiple: false,
            disabled,
        });

        const inputProps = getInputProps();

        return (
            <LogoUploadBox {...getRootProps()} disabled={disabled} {...(disabled ? { onClick: onClickUpgrade } : {})}>
                <LogoUploadBoxInner>
                    <>
                        {file ? (
                            <>
                                <LogoThumbnail src={thumbnail} />

                                <div>
                                    <FilenameText variant="h4">{file.name}</FilenameText>
                                    <Typography variant="body2" color={theme.colors.secondary[500]}>
                                        {(file.size / 1000).toFixed()}KB
                                    </Typography>
                                </div>
                                <ClearButton ariaLabel="close" onClick={handleClearFile}>
                                    <CloseIcon />
                                </ClearButton>
                            </>
                        ) : (
                            <>
                                <input
                                    {...inputProps}
                                    onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                                        inputProps?.onBlur?.(event);
                                        onBlur?.();
                                    }}
                                    ref={ref}
                                />
                                <MaskedIcon src={GalleryAdd} color={theme.colors.secondary[500]} size="24px" />

                                <div>
                                    <Typography variant="h4">Drag logo image or click here to upload</Typography>
                                    <Typography variant="body2" color={theme.colors.secondary[500]}>
                                        PNG/JPG up to 5MB
                                    </Typography>
                                </div>
                            </>
                        )}
                    </>
                </LogoUploadBoxInner>
            </LogoUploadBox>
        );
    }
);
